import React from 'react';

// Design System Components
import Search from 'Components/DesignSystem/Search/Search';
import Button from 'Components/DesignSystem/Button/Button';
import DropdownButton from 'Components/DesignSystem/DropdownButton/DropdownButton';
import { Edit, RowAction } from 'Components/DesignSystem/Iconography/Icons';

// Types
import { ActionBarProps } from '../../types';

/**
 * ActionBar Component
 *
 * Displays search, bulk actions dropdown, filter, and edit toggle
 * Used at the top of tab content for controlling table actions
 */
const ActionBar = ({
  searchValue,
  onSearchChange,
  onSearch,
  bulkActions = [],
  onBulkAction,
  onFilterClick,
  isEditMode = false,
  onEditToggle,
  showEditButton = true,
  selectedCount = 0,
  isLoading = false,
  isSearching = false,
  hasUpdatePermission = true,
  showBulkAllButton = false,
  bulkAllLabel = '',
  onBulkAllClick,
  isBulkAllDisabled = false,
  className = '',
}: ActionBarProps) => {
  // Transform bulk actions for DropdownButton format
  const dropdownOptions = bulkActions.map((action) => ({
    id: action.value,
    label: action.label,
    disabled: !hasUpdatePermission || selectedCount === 0 || isLoading,
  }));

  const handleBulkAction = (actionId: string | number) => {
    onBulkAction?.(String(actionId));
  };

  return (
    <div className={`tw-flex tw-items-center tw-justify-between tw-gap-4 tw-mb-4 ${className}`}>
      {/* Left side: Search */}
      <div className="tw-flex-1 tw-max-w-md">
        <Search
          placeholder="Search"
          value={searchValue}
          onChange={onSearchChange}
          onSearch={onSearch}
          /* § 288 — `isSearching`, NOT `isLoading`: the latter means a bulk action is
             in flight and gates the bulk controls beside this field. The caller owns
             the expression because only it knows its own debounce. */
          loading={isSearching}
          loadingLabel="Searching products"
          size="medium"
          variant="bordered"
        />
      </div>

      {/* Right side: Actions */}
      <div className="tw-flex tw-items-center tw-gap-2">
        {/* Whole-tab bulk button (13765): "Update All" / "Create All".
            Hidden — not disabled — without the bulkupdate permission (the
            caller gates showBulkAllButton on it, per AC 3). */}
        {showBulkAllButton && (
          <Button
            /* `secondary`, not `primary` (2026-08-31). This bar had TWO filled
               primaries — this one and the Edit toggle — and a toolbar gets one.
               `Update All` is the strongest action available while not editing, so
               it leads with the accent outline; the fill is spent on Save, in the
               editing banner, which is the action a user actually has to press. */
            variant="secondary"
            onClick={onBulkAllClick}
            disabled={isBulkAllDisabled || isLoading}
            data-testid="catalog-bulk-all-btn"
          >
            {bulkAllLabel}
          </Button>
        )}

        {/* Bulk Actions Dropdown */}
        {bulkActions.length > 0 && (
          /* `subtle`, which is the design system's own name for this exact control:
             "the quiet toolbar trigger (§ 65) — Sort / Filter / Bulk Action. Ink,
             not accent." It replaces a `[&_button]:tw-text-ink` wrapper that
             reached into the component to repaint `tertiary`'s accent ink — same
             #172b4d, but hand-applied, so it also overrode the DISABLED ink and left
             the hover as `tertiary`'s accent tint. */
          <DropdownButton
            label={`Row Actions${selectedCount > 0 ? ` (${selectedCount})` : ''}${isLoading ? ' ...' : ''}`}
            options={dropdownOptions}
            onSelect={handleBulkAction}
            variant="subtle"
            disabled={!hasUpdatePermission || selectedCount === 0 || isLoading}
            icon={<RowAction />}
            showChevron={false}
          />
        )}

        {/* Filter Button */}
        {/* <Button
          variant="tertiary"
          icon={<Filter />}
          onClick={onFilterClick}
          className="tw-text-ink"
        >
          Filter
        </Button> */}

        {/* Edit Toggle Button - Conditionally rendered */}
        {showEditButton && (
          <Button
            /*
              Off -> `subtle` (was `tertiary` + a `tw-text-ink` override, which
              is what `subtle` already is). On -> `secondary`, not `primary`.

              A blue-FILLED "Editing" sat directly above a blue-filled "Save" in the
              editing banner, so the screen showed two identical primary objects and
              only one of them was the action. The banner IS the mode indicator; the
              toggle only has to read as on, which the accent outline and accent ink
              do against `subtle`'s plain ink.
            */
            variant={isEditMode ? 'secondary' : 'subtle'}
            icon={<Edit />}
            onClick={onEditToggle}
            disabled={!hasUpdatePermission}
            aria-pressed={isEditMode}
            data-testid="catalog-edit-toggle-btn"
          >
            {isEditMode ? 'Editing' : 'Edit'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ActionBar;
